import hotkeys, { HotkeysEvent, KeyHandler } from 'hotkeys-js';
import { DependencyList, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Allow all keys through (we do our own filtering)
hotkeys.filter = () => true;
const blockTags = ['INPUT', 'TEXTAREA', 'SELECT'];
const blockClasses = ['.ck-editor__editable'];

export { hotkeys };

type History = ReturnType<typeof useNavigate>;

export enum KeyDirection {
  DOWN,
  UP,
  BOTH
}
export type HotkeyOptions = {
  direction?: KeyDirection; // Default DOWN
  checkBlockTags?: boolean | null; // Default true
};

class HotkeyManager {
  private static instance: HotkeyManager;
  private history: History;

  private navigationRoutes: Record<string, string> = {};
  private registeredKeys: Record<string, Array<KeyHandler>> = {};

  static getInstance(history: History): HotkeyManager {
    if (!HotkeyManager.instance) HotkeyManager.instance = new HotkeyManager(history);
    return HotkeyManager.instance;
  }

  getRegisteredKeys() {
    return Object.keys(this.registeredKeys);
  }

  private bind(key: string, callback: KeyHandler, options?: HotkeyOptions) {
    const transformedOptions = {
      keydown: options?.direction !== KeyDirection.UP,
      keyup: options?.direction === KeyDirection.UP || options?.direction === KeyDirection.BOTH
    };

    const metaKey = navigator.platform.startsWith('Mac') ? 'Command' : 'Ctrl';
    key = key.replace('meta', metaKey);

    hotkeys(key, transformedOptions, (keyboardEvent: KeyboardEvent, hotkeyEvent: HotkeysEvent) => {
      if (options?.checkBlockTags) {
        const target = (keyboardEvent.target ?? keyboardEvent.srcElement) as HTMLElement;
        if (this.inBlockedTag(target, keyboardEvent)) return;
      }
      callback(keyboardEvent, hotkeyEvent);
      // Don't return anything so that we don't block browser hotkeys
    });
  }

  private inBlockedTag(target: HTMLElement, keyboardEvent: KeyboardEvent): boolean {
    if (
      target &&
      target.tagName &&
      (target.isContentEditable ||
        blockTags.includes(target.tagName) ||
        target.classList.contains('ck-content'))
    ) {
      return true;
    }
    return false;
  }

  private gKeyTimestamp: number | undefined;
  constructor(history: History) {
    this.history = history;

    // Handle navigation keys
    this.bind('*', (evt: KeyboardEvent) => {
      const target = (evt.target ?? evt.srcElement) as HTMLElement;
      if (this.inBlockedTag(target, evt)) return;
      if (evt.key === 'g') this.gKeyTimestamp = Date.now();
      else if (
        this.navigationRoutes.hasOwnProperty(evt.key) &&
        this.gKeyTimestamp &&
        Date.now() - this.gKeyTimestamp < 500
      )
        this.history(this.navigationRoutes[evt.key]);
      return false;
    });
  }

  registerNavigateRoute(key: string, route: string) {
    this.navigationRoutes[key] = route;
  }

  registerHotkey(key: string, callback: KeyHandler, options?: HotkeyOptions): () => void {
    if (this.registeredKeys.hasOwnProperty(key)) {
      // If already listening to this hotkey, unbind it
      if (this.registeredKeys[key].length > 0) {
        // eslint-disable-next-line
        // @ts-ignore This works, but TypeScript does not know about it...
        hotkeys.unbind({ key });
      }
    } else {
      this.registeredKeys[key] = [];
    }

    // These two types should take affect regardless of if there is an input element focused
    options = {
      ...options,
      checkBlockTags: !(key === HotkeyType.PRIMARY || key === HotkeyType.CANCEL)
    };

    // Bind new callback and add to start of array
    this.registeredKeys[key].unshift(callback);
    this.bind(key, callback, options);

    // Return a function that removes this handler and re-binds the previous one
    return () => {
      const index = this.registeredKeys[key].indexOf(callback);
      this.registeredKeys[key].splice(index, 1);

      // eslint-disable-next-line
      // @ts-ignore This works, but TypeScript does not know about it...
      hotkeys.unbind({ key });

      // If this is removing the active callback, bind the next one in the stack
      if (index === 0 && this.registeredKeys[key].length > 0) {
        this.bind(key, this.registeredKeys[key][0], options);
      }
    };
  }
}

export const HotkeyType = {
  HELP: 'shift+/',
  ADD: 'shift+=',
  PRIMARY: 'ctrl+Enter, cmd+Enter',
  CANCEL: 'Escape'
};

export function useHotkeyManager(): HotkeyManager {
  const history = useNavigate();
  return HotkeyManager.getInstance(history);
}

export function useHotkey(
  key: string,
  callback: KeyHandler,
  options?: HotkeyOptions,
  dependencies: DependencyList = []
): void {
  const manager = useHotkeyManager();
  useEffect(
    () => manager.registerHotkey(key, callback, options),
    [manager, key, callback, options, ...dependencies]
  );
}
